/**
 * Copiloto de IA da vendedora: sugestões de resposta que ela revisa antes de
 * enviar. A IA nunca envia nada sozinha.
 */
import { z } from "zod";

export const ACOES_COPILOTO = [
  "sugerir",
  "mais_curta",
  "mais_simpatica",
  "mais_comercial",
  "alternativa",
  "fechar_venda",
] as const;
export type AcaoCopiloto = (typeof ACOES_COPILOTO)[number];

export const ROTULO_ACAO_COPILOTO: Record<AcaoCopiloto, string> = {
  sugerir: "Sugerir resposta",
  mais_curta: "Mais curta",
  mais_simpatica: "Mais simpática",
  mais_comercial: "Mais comercial",
  alternativa: "Oferecer alternativa",
  fechar_venda: "Tentar fechar venda",
};

/** Ações que reescrevem um texto (o do compositor ou uma sugestão). */
export const ACOES_REESCRITA: readonly AcaoCopiloto[] = [
  "mais_curta",
  "mais_simpatica",
  "mais_comercial",
];

export interface SugestaoIa {
  texto: string;
}

export interface SugestoesIa {
  interacaoId: string;
  acao: AcaoCopiloto;
  sugestoes: SugestaoIa[];
  /** Quantas vezes a IA consultou o estoque do ERP para sugerir. */
  consultasEstoque: number;
  criadoEm: string;
}

export interface EstadoCopiloto {
  disponivel: boolean;
  automatico: boolean;
  /** Sugestões ainda valendo (geradas depois da última resposta da loja). */
  atual: SugestoesIa | null;
}

export const pedirSugestoesSchema = z.object({
  acao: z.enum(ACOES_COPILOTO).default("sugerir"),
  /** Texto a reescrever (obrigatório nas ações de reescrita). */
  base: z.string().trim().max(4096).nullish(),
});
export type PedirSugestoes = z.infer<typeof pedirSugestoesSchema>;

export const AVALIACOES_IA = [
  "otima",
  "nao_usar",
  "resposta_padrao",
  "informacao_incorreta",
  "tom_inadequado",
] as const;
export type AvaliacaoIa = (typeof AVALIACOES_IA)[number];

export const ROTULO_AVALIACAO_IA: Record<AvaliacaoIa, string> = {
  otima: "Ótima resposta",
  nao_usar: "Não usar",
  resposta_padrao: "Resposta padrão",
  informacao_incorreta: "Informação incorreta",
  tom_inadequado: "Tom inadequado",
};

export const avaliarSchema = z.object({
  avaliacao: z.enum(AVALIACOES_IA),
  comentario: z.string().trim().max(500).nullish(),
});

/** Uso de uma sugestão ao enviar (aprendizado: usada como veio ou editada). */
export const usoSugestaoSchema = z.object({
  interacaoId: z.string().uuid(),
  indice: z.number().int().min(0).max(2),
  editada: z.boolean(),
});
export type UsoSugestao = z.infer<typeof usoSugestaoSchema>;

export const configIaSchema = z.object({
  /** Gerar sugestões sozinho a cada mensagem da cliente. */
  sugestoesAutomaticas: z.boolean().default(true),
  /** Procurar no estoque sozinho quando a cliente manda foto. */
  identificarFotos: z.boolean().default(true),
  /** Jeito de falar da YourDress, em texto livre (entra em toda sugestão). */
  orientacoes: z.string().trim().max(4000).default(""),
});
export type ConfigIa = z.infer<typeof configIaSchema>;
export const CONFIG_IA_PADRAO: ConfigIa = {
  sugestoesAutomaticas: true,
  identificarFotos: true,
  orientacoes: "",
};

/** Informação oficial da loja (a IA sempre privilegia). */
export const CATEGORIAS_CONHECIMENTO = [
  "Trocas e devoluções",
  "Entregas e frete",
  "Pagamento",
  "Horários e endereço",
  "Tamanhos e medidas",
  "Promoções e campanhas",
  "Orientações comerciais",
  "Outros",
] as const;

export const informacaoOficialSchema = z.object({
  categoria: z.enum(CATEGORIAS_CONHECIMENTO),
  titulo: z.string().trim().min(2).max(120),
  conteudo: z.string().trim().min(2).max(2000),
  ativo: z.boolean().default(true),
  /** Promoções com prazo: some sozinha depois desta data (AAAA-MM-DD). */
  vigenteAte: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullish(),
});
export type InformacaoOficialEntrada = z.infer<typeof informacaoOficialSchema>;

export interface InformacaoOficial extends InformacaoOficialEntrada {
  id: string;
  atualizadoEm: string;
}
